import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ActivitiesContext } from "./ActivitiesContext";
import useActivitiesAPIClient from "../../api/activities/hooks/useActivitiesAPIClient";
import {
  addActivity,
  updateActivity as updateActivityAction,
  removeActivity,
} from "../../redux/actions/activities";
import { setOverlayVisible } from "../../redux/actions/overlay";
import { DEFAULT_ACTIVITY_COLOR } from "../../constants/pickerColors";
import { getRandomColor } from "../../helpers/colorUtils";
import { ACTIVITY_COLORS } from "../../constants/activityColors";

const ActivitiesContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { activities: activitiesMap, loaded } = useSelector(
    (state) => state.activities,
  );
  const {
    createActivity,
    updateActivity: updateActivityAPI,
    deleteActivity,
  } = useActivitiesAPIClient();

  const activities = useMemo(
    () => Object.values(activitiesMap),
    [activitiesMap],
  );

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [activityName, setActivityName] = useState("");
  const [activityEmoji, setActivityEmoji] = useState("");
  const [activityDescription, setActivityDescription] = useState("");
  const [activityColor, setActivityColor] = useState(
    getRandomColor(ACTIVITY_COLORS),
  );
  const [activityDuration, setActivityDuration] = useState(null);
  const [saving, setSaving] = useState(false);

  // Emoji picker state
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);

  // Color picker state
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);

  // Random selector state
  const [randomDialogOpen, setRandomDialogOpen] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [currentActivityIndex, setCurrentActivityIndex] = useState(0);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [spinSpeed, setSpinSpeed] = useState(100);
  const spinIntervalRef = useRef(null);
  const activitiesRef = useRef(activities);

  // Keep activitiesRef in sync with activities
  useEffect(() => {
    activitiesRef.current = activities;
  }, [activities]);

  // Dialog handlers
  const resetDialog = useCallback(() => {
    setEditingActivity(null);
    setActivityName("");
    setActivityEmoji("");
    setActivityDescription("");
    setActivityColor(getRandomColor(ACTIVITY_COLORS));
    setActivityDuration(null);
  }, []);

  const openCreateDialog = useCallback(() => {
    resetDialog();
    setDialogOpen(true);
  }, [resetDialog]);

  const openEditDialog = useCallback((activity) => {
    setEditingActivity(activity);
    setActivityName(activity.name);
    setActivityEmoji(activity.emoji || "");
    setActivityDescription(activity.description || "");
    setActivityColor(activity.color || DEFAULT_ACTIVITY_COLOR);
    setActivityDuration(activity.duration || null);
    setDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    resetDialog();
  }, [resetDialog]);

  const handleSave = useCallback(async () => {
    if (!activityName.trim()) return;

    setSaving(true);
    try {
      const activityData = {
        name: activityName.trim(),
        emoji: activityEmoji.trim() || undefined,
        description: activityDescription.trim() || undefined,
        color: activityColor,
        duration: activityDuration || undefined,
      };

      if (editingActivity) {
        await updateActivityAPI(editingActivity.activityId, activityData);
        dispatch(
          updateActivityAction(editingActivity.activityId, activityData),
        );
      } else {
        const response = await createActivity(activityData);
        dispatch(addActivity(response.activity));
      }
      closeDialog();
    } catch (error) {
      console.error("Failed to save activity:", error);
    }
    setSaving(false);
  }, [
    activityName,
    activityEmoji,
    activityDescription,
    activityColor,
    activityDuration,
    editingActivity,
    updateActivityAPI,
    createActivity,
    dispatch,
    closeDialog,
  ]);

  const handleDelete = useCallback(
    (activity) => {
      dispatch(
        setOverlayVisible({
          visible: true,
          overlayType: "confirm",
          confirmationText: `Are you sure you want to delete "${activity.name}"?`,
          confirmCallback: async () => {
            try {
              await deleteActivity(activity.activityId);
              dispatch(removeActivity(activity.activityId));
              dispatch(setOverlayVisible({ visible: false }));
            } catch (error) {
              console.error("Failed to delete activity:", error);
              dispatch(setOverlayVisible({ visible: false }));
            }
          },
        }),
      );
    },
    [dispatch, deleteActivity],
  );

  // Color picker handlers
  const handleColorClick = useCallback((event) => {
    setColorPickerAnchor(event.currentTarget);
  }, []);

  const handleColorPickerClose = useCallback(() => {
    setColorPickerAnchor(null);
  }, []);

  const handleColorSelect = useCallback((color) => {
    setActivityColor(color);
    setColorPickerAnchor(null);
  }, []);

  // Random selector handlers
  const openRandomSelector = useCallback(() => {
    if (activities.length === 0) return;
    setRandomDialogOpen(true);
    setSelectedActivity(null);
    setIsSpinning(false);
    setCurrentActivityIndex(0);
  }, [activities.length]);

  const closeRandomSelector = useCallback(() => {
    setRandomDialogOpen(false);
    if (spinIntervalRef.current) {
      clearInterval(spinIntervalRef.current);
    }
  }, []);

  const startSpin = useCallback(() => {
    const currentActivities = activitiesRef.current;
    if (currentActivities.length === 0) return;

    setIsSpinning(true);
    setSelectedActivity(null);

    let speed = 100;
    setSpinSpeed(speed);
    let iteration = 0;
    const maxIterations = 20 + Math.floor(Math.random() * 10);
    const finalIndex = Math.floor(Math.random() * currentActivities.length);

    const spin = () => {
      const latestActivities = activitiesRef.current;
      setCurrentActivityIndex((prev) => (prev + 1) % latestActivities.length);
      iteration++;

      if (iteration >= maxIterations) {
        setCurrentActivityIndex(finalIndex);
        setSelectedActivity(latestActivities[finalIndex]);
        setIsSpinning(false);
        if (spinIntervalRef.current) {
          clearInterval(spinIntervalRef.current);
        }
        return;
      }

      // Progressive slowdown - gradually increase speed as we approach the end
      const progress = iteration / maxIterations;
      if (progress > 0.4) {
        // Exponential slowdown for smooth deceleration
        const slowdownFactor = Math.pow((progress - 0.4) / 0.6, 2);
        speed = 100 + slowdownFactor * 300;
        setSpinSpeed(speed);

        if (spinIntervalRef.current) {
          clearInterval(spinIntervalRef.current);
        }
        spinIntervalRef.current = setInterval(spin, speed);
      }
    };

    spinIntervalRef.current = setInterval(spin, speed);
  }, []);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (spinIntervalRef.current) {
        clearInterval(spinIntervalRef.current);
      }
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      // Data
      activities,
      loaded,

      // Dialog state
      dialogOpen,
      editingActivity,
      openCreateDialog,
      openEditDialog,
      closeDialog,

      // Dialog form
      activityName,
      setActivityName,
      activityEmoji,
      setActivityEmoji,
      activityDescription,
      setActivityDescription,
      activityColor,
      setActivityColor,
      activityDuration,
      setActivityDuration,
      saving,
      handleSave,

      // Delete
      handleDelete,

      // Emoji picker
      emojiPickerOpen,
      setEmojiPickerOpen,

      // Color picker
      colorPickerAnchor,
      handleColorClick,
      handleColorPickerClose,
      handleColorSelect,

      // Random selector
      randomDialogOpen,
      openRandomSelector,
      closeRandomSelector,
      isSpinning,
      currentActivityIndex,
      selectedActivity,
      spinSpeed,
      startSpin,
    }),
    [
      activities,
      loaded,
      dialogOpen,
      editingActivity,
      openCreateDialog,
      openEditDialog,
      closeDialog,
      activityName,
      activityEmoji,
      activityDescription,
      activityColor,
      activityDuration,
      saving,
      handleSave,
      handleDelete,
      emojiPickerOpen,
      colorPickerAnchor,
      handleColorClick,
      handleColorPickerClose,
      handleColorSelect,
      randomDialogOpen,
      openRandomSelector,
      closeRandomSelector,
      isSpinning,
      currentActivityIndex,
      selectedActivity,
      spinSpeed,
      startSpin,
    ],
  );

  return (
    <ActivitiesContext.Provider value={contextValue}>
      {children}
    </ActivitiesContext.Provider>
  );
};

export default ActivitiesContextProvider;
